<!-- dynamic back button render -->
<ng-content select="[headerElement]"></ng-content>
<div
  #scrollContainer
  [ngClass]="{
    'scroll-container': activities?.events?.length > 0
  }"
  infiniteScroll
  [scrollWindow]="false"
  [infiniteScrollDistance]="4"
  [infiniteScrollUpDistance]="3"
  [infiniteScrollThrottle]="100"
  (scrolled)="onScrollDown($event)"
  aria-live="polite"
  role="feed"
  aria-label="Activity feed with infinite scroll"
  aria-relevant="additions"
>
  <div class="activity-container w-100 h-100">
    <ng-container
      *ngFor="
        let activity of activities?.events | orderBy : '-timestamp';
        let i = index;
        trackBy: trackByOption
      "
    >
      <!-- Events with accessPointName: apply access control -->
      <ng-container
        *ngIf="
          activity?.accessPointName !== '' &&
          activity?.accessPointName !== null &&
          activity?.accessPointName !== undefined
        "
      >
      
        <ng-container *appHideAccess="commonAccessControl">
          <ng-container
            *ngTemplateOutlet="
              activityCardTpl;
              context: { $implicit: activity }
            "
          ></ng-container>
        </ng-container>
      </ng-container>

      <!-- Events without accessPointName: always visible -->
      <ng-container
        *ngIf="
          activity?.accessPointName === '' ||
          activity?.accessPointName === null ||
          activity?.accessPointName === undefined
        "
      >
        <ng-container
          *ngTemplateOutlet="activityCardTpl; context: { $implicit: activity }"
        ></ng-container>
      </ng-container>
    </ng-container>

    <!-- Load more spinner -->
    <div class="load-more-container" *ngIf="canLoad">
      <div class="spinner-small"></div>
      <span>Loading more...</span>
    </div>

    <div #sentinel class="scroll-sentinel" style="height: 1px"></div>
  </div>

  <ng-container *ngIf="activities?.events?.length < 1">
    <div class="content h-100">
      <div
        class="d-flex flex-column justify-content-center align-items-center h-100"
      >
        <div
          class="text-container d-flex flex-column justify-content-center align-items-center"
        >
          <div class="icon-container">
            <img src="assets/svg/ico-lock.svg" class="lock-icon" />
          </div>
          <div class="text-center mt-3">
            <p>{{ "NO_ACTIVITIES_AVAILABLE" | translate }}</p>
          </div>
        </div>
      </div>
    </div>
  </ng-container>
</div>

<!-- Activity card template -->
<ng-template #activityCardTpl let-activity>
  <div class="activity-card" [ngClass]="getSeverityClass(activity)">
    <div class="activity-card-wrapper">
      <div class="activity-content">
        <div class="activity-icon-section">
          <img
            [src]="getEventIcon(activity)"
            alt="icon"
            class="activity-icon"
          />
        </div>

        <div class="activity-details">
          <div class="activity-title">
            <div class="lock-name-wrap">
              <app-with-tooltip [showHelpCursor]="true">
                {{ activity?.deviceName }}
              </app-with-tooltip>
            </div>
            <span
              class="activity-title-status"
              *ngIf="activity?.eventTitle || activity?.eventDescription"
            >
              <app-with-tooltip [showHelpCursor]="true"
                >:
                {{
                  activity?.eventTitle || activity?.eventDescription
                }}</app-with-tooltip
              >
            </span>
          </div>

          <!-- Extra description: for specific codes or issue events -->
          <div
            class="activity-description"
            *ngIf="activity?.extraDescription && hasExtraDescription(activity)"
          >
            <app-with-tooltip
              [showHelpCursor]="true"
              class="description-text"
              >{{ activity?.extraDescription }}</app-with-tooltip
            >
          </div>

          <!-- Source line -->
          <div class="activity-source" *ngIf="activity?.source">
            <img
              src="assets/svg/ico-lock-access-settings.svg"
              alt="icon"
              class="source-icon"
            />
            <app-with-tooltip [showHelpCursor]="true" class="source-text">{{
              getSourceDisplay(activity)
            }}</app-with-tooltip>
          </div>

          <!-- User name -->
          <div class="activity-user" *ngIf="activity?.user">
            <app-with-tooltip [showHelpCursor]="true" class="user-text">{{
              activity?.user
            }}</app-with-tooltip>
          </div>
        </div>

        <div class="activity-metadata">
          <div class="activity-date">
            {{ activity?.isoTime | parsedActivityDateTime : "MMM dd" : true }}
          </div>
          <div class="activity-time">
            {{ activity?.isoTime | parsedDateTime : "hh:mm a" }}
          </div>
        </div>
      </div>

      <!-- Collapsed location bar -->
      <div
        class="activity-location"
        *ngIf="
          expandedActivityId !== activity?.id &&
          (activity?.buildingName || activity?.unitName)
        "
      >
        <div class="location-items">
          <div class="location-group" *ngIf="activity?.buildingName">
            <span class="location-icon">
              <img src="assets/svg/ico-building-gray.svg" alt="icon" />
            </span>
            <span class="location-name">
              <app-with-tooltip [showHelpCursor]="true">{{
                activity?.buildingName
              }}</app-with-tooltip>
            </span>
          </div>
          <div class="location-group" *ngIf="activity?.unitName">
            <span class="location-icon">
              <img src="assets/svg/ico-default-home.svg" alt="icon" />
            </span>
            <span class="location-name">
              <app-with-tooltip [showHelpCursor]="true">{{
                activity?.unitName
              }}</app-with-tooltip>
            </span>
          </div>
        </div>
        <span
          class="chevron-dropdown-container"
          (click)="toggleDropdown(activity?.id)"
        >
          <img
            src="assets/svg/ico-chevron-down-outline-all-acces.svg"
            class="pointer-cursor chevron-icon"
          />
        </span>
      </div>

      <!-- Expanded location details -->
      <div
        class="activity-location-expanded"
        *ngIf="
          expandedActivityId === activity?.id &&
          (activity?.buildingName || activity?.unitName)
        "
      >
        <div class="expanded-header">
          <span
            class="chevron-dropdown-container"
            (click)="toggleDropdown(activity?.id)"
          >
            <img
              src="assets/svg/ico-up-arrow.svg"
              class="pointer-cursor chevron-icon"
            />
          </span>
        </div>
        <div class="dropdown-detail-item" *ngIf="activity?.buildingName">
          <img
            src="assets/svg/ico-building-gray.svg"
            alt="building"
            class="dropdown-detail-icon"
          />
          <span class="dropdown-detail-text">{{ activity?.buildingName }}</span>
        </div>
        <div class="dropdown-detail-item" *ngIf="activity?.floorName">
          <img
            src="assets/svg/ico-floor.svg"
            alt="floor"
            class="dropdown-detail-icon"
          />
          <span class="dropdown-detail-text">{{ activity?.floorName }}</span>
        </div>
        <div class="dropdown-detail-item" *ngIf="activity?.wingName">
          <img
            src="assets/svg/ico-direction.svg"
            alt="wing"
            class="dropdown-detail-icon"
          />
          <span class="dropdown-detail-text">{{ activity?.wingName }}</span>
        </div>
        <div class="dropdown-detail-item" *ngIf="activity?.unitName">
          <img
            src="assets/svg/ico-default-home.svg"
            alt="unit"
            class="dropdown-detail-icon"
          />
          <span class="dropdown-detail-text">{{ activity?.unitName }}</span>
        </div>
      </div>
    </div>
  </div>
</ng-template>
